import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Reply, ReplyAll, Forward, Trash2, Mail, Star, FolderInput, Paperclip, Download, Eye, CalendarPlus, CalendarCheck,
  Video, MapPin, Clock, UserPlus, X, Loader2, AlarmClock, Check, Pencil, ChevronDown,
} from 'lucide-react';
import { email, baixarAnexo, tamanho, dataHora, diaCurto, nomeDe, escapar, quando } from './api';

const ORIGEM = { iw: 'IW', captacao: 'Captação' };
const CORES = ['#42796e', '#2563eb', '#9333ea', '#db2777', '#ea580c', '#0d9488', '#4f46e5', '#b45309'];
const corDe = s => CORES[[...String(s || '')].reduce((a, ch) => a + ch.charCodeAt(0), 0) % CORES.length];

function Avatar({ pessoa }) {
  const nome = pessoa?.nome || pessoa?.email || '?';
  return <span className="em-avatar" style={{ background: corDe(pessoa?.email || nome) }}>{nome.replace(/[^A-Za-zÀ-ú]/g, '').charAt(0).toUpperCase() || '?'}</span>;
}

/** Ligar o e-mail a um paciente: busca no IW (em atendimento/último ano) e nos cards da Captação. */
function BuscarPaciente({ onEscolher, onFechar }) {
  const [busca, setBusca] = useState('');
  const [achados, setAchados] = useState(null);
  useEffect(() => {
    if (busca.trim().length < 3) { setAchados(null); return undefined; }
    const t = setTimeout(() => email('pacientes', { busca }).then(r => setAchados([...(r.comEmails || []), ...(r.encontrados || [])])).catch(() => setAchados([])), 350);
    return () => clearTimeout(t);
  }, [busca]);
  return (
    <div className="em-popover">
      <div className="em-popover-cab"><strong>Ligar a um paciente</strong><button className="em-icone" onClick={onFechar}><X size={14} /></button></div>
      <input autoFocus placeholder="Nome do paciente (mín. 3 letras)" value={busca} onChange={e => setBusca(e.target.value)} />
      <div className="em-popover-lista">
        {achados === null && <div className="em-sub">Pacientes do IW (em atendimento ou com movimento no último ano) e cards da Captação.</div>}
        {achados?.length === 0 && <div className="em-sub">Ninguém encontrado.</div>}
        {achados?.map(p => (
          <button key={p.id} onClick={() => onEscolher(p)}>
            <strong>{p.nome}</strong>
            <span className="em-sub">{(p.origens || [p.origem]).map(o => ORIGEM[o]).join(' + ')}{p.convenio ? ` · ${p.convenio}` : ''}{p.emAtendimento ? ' · em atendimento' : ''}{p.status ? ` · ${p.status}` : ''}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** Cartão da reunião (convite .ics ou reunião citada no texto). */
function CartaoReuniao({ indice, eu, onAgenda }) {
  const c = indice.convite || {};
  const [editando, setEditando] = useState(false);
  const [f, setF] = useState({ titulo: c.titulo || '', data: c.data || '', horario: c.horario || '09:00', horarioFim: c.horarioFim || '', link: c.link || '' });
  const [salvando, setSalvando] = useState(false);
  const naMinha = (indice.agenda || []).some(a => a.uid === eu.uid);
  const nomes = (indice.agenda || []).map(a => a.nome);
  async function adicionar() {
    setSalvando(true);
    try { await onAgenda(indice.id, f); setEditando(false); } catch (_) { /* aviso já mostrado */ } finally { setSalvando(false); }
  }
  return (
    <div className={`em-cartao-reuniao ${indice.tipo === 'cancelamento' ? 'em-cancelada' : ''}`}>
      <div className="em-cartao-titulo">
        <Video size={16} />
        <strong>{indice.tipo === 'cancelamento' ? 'Reunião CANCELADA' : c.sugestao ? 'Reunião citada no e-mail (sem convite)' : 'Convite de reunião'}</strong>
      </div>
      <div className="em-cartao-linhas">
        <div><strong>{c.titulo}</strong></div>
        <div><Clock size={13} /> {diaCurto(c.data)} {c.diaInteiro ? '(dia inteiro)' : `${c.horario}${c.horarioFim ? ` – ${c.horarioFim}` : ''}`}{c.repete ? ' · repete' : ''}</div>
        {c.local && <div><MapPin size={13} /> {c.local}</div>}
        {c.organizador && <div className="em-sub">Organizador: {c.organizador.nome || c.organizador.email}</div>}
        {c.participantes?.length > 0 && <div className="em-sub">Participantes: {c.participantes.map(p => p.nome || p.email).join(', ')}</div>}
        {c.repete && c.ocorrencias?.length > 1 && <div className="em-sub">Próximas: {c.ocorrencias.slice(0, 6).map(o => `${diaCurto(o.data)} ${o.hora}`).join(' · ')}</div>}
      </div>
      <div className="em-cartao-acoes">
        {c.link && indice.tipo !== 'cancelamento' && <a className="em-btn em-btn-primario" href={c.link} target="_blank" rel="noopener noreferrer"><Video size={14} /> Entrar na reunião</a>}
        {indice.tipo !== 'cancelamento' && (naMinha
          ? <span className="em-tag em-tag-verde"><CalendarCheck size={13} /> Na sua agenda</span>
          : <button className="em-btn" onClick={() => setEditando(true)} disabled={salvando}><CalendarPlus size={14} /> Pôr na minha agenda</button>)}
        {nomes.length > 0 && <span className="em-sub">Na agenda de: {nomes.join(', ')}</span>}
      </div>
      {editando && (
        <div className="em-form-grade em-cartao-form">
          <label>Título<input value={f.titulo} onChange={e => setF({ ...f, titulo: e.target.value })} /></label>
          <label>Data<input type="date" value={f.data} onChange={e => setF({ ...f, data: e.target.value })} /></label>
          <label>Início<input type="time" value={f.horario} onChange={e => setF({ ...f, horario: e.target.value })} /></label>
          <label>Fim<input type="time" value={f.horarioFim} onChange={e => setF({ ...f, horarioFim: e.target.value })} /></label>
          <label className="em-form-linha">Link<input value={f.link} onChange={e => setF({ ...f, link: e.target.value })} /></label>
          <div className="em-form-botoes">
            <button className="em-btn em-btn-primario" onClick={adicionar} disabled={salvando}>{salvando ? <Loader2 size={14} className="em-gira" /> : <CalendarPlus size={14} />} Confirmar na agenda</button>
            <button className="em-btn" onClick={() => setEditando(false)}>Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Separa o texto novo do e-mail do histórico citado (respostas anteriores coladas embaixo — Outlook,
 * Gmail, celular). Devolve { principal (HTML), citado: true } ou null se não achar citação.
 * Só lê o HTML (DOMParser não executa nada).
 */
const RE_CAB_CITACAO = /^\s*(De|From)\s*:/i;
const RE_CAB_RESTO = /(Enviad[oa]|Sent|Data|Date)\s*(em)?\s*:[\s\S]{0,400}(Assunto|Subject)\s*:/i;
const RE_ESCREVEU = /(escreveu|wrote)\s*:\s*$/i;
function separarCitacao(html, texto) {
  if (!html) {
    const t = String(texto || '');
    const m = t.match(/\n[ \t]*(?:-{2,}\s*(?:Mensagem original|Original Message|Mensagem encaminhada|Forwarded message)|(?:De|From):[^\n]*\n[^\n]*(?:Enviad[oa]|Sent|Data|Date)\s*(?:em)?:|Em [^\n]{5,160}escreveu:|On [^\n]{5,160}wrote:)/i);
    const i = m ? m.index : t.search(/\n\s*>/);
    if (i <= 0 || !t.slice(0, i).trim()) return null;
    return { principal: `<pre style="white-space:pre-wrap;font-family:inherit;margin:0">${escapar(t.slice(0, i).trimEnd())}</pre>`, citado: true };
  }
  let doc;
  try { doc = new DOMParser().parseFromString(String(html), 'text/html'); } catch (_) { return null; }
  const body = doc.body;
  if (!body) return null;
  // 1) marcas conhecidas dos programas de e-mail
  let corte = body.querySelector('div.gmail_quote, blockquote[type="cite"], [id^="divRplyFwdMsg"], #appendonsend, div.OutlookMessageHeader, div.moz-cite-prefix, div.yahoo_quoted');
  // 2) bloco "De: … Enviado: … Assunto: …" (Outlook / Skymail)
  if (!corte) {
    for (const el of body.querySelectorAll('p, div, span, b, font, td')) {
      const txt = el.textContent || '';
      if (!RE_CAB_CITACAO.test(txt) || !RE_CAB_RESTO.test(txt.slice(0, 900))) continue;
      corte = el;
      // sobe enquanto o bloco de cima começa pelo mesmo "De:"
      while (corte.parentElement && corte.parentElement !== body && RE_CAB_CITACAO.test(corte.parentElement.textContent || '')) corte = corte.parentElement;
      break;
    }
  }
  if (!corte) return null;
  // leva junto a linha separadora (<hr>) ou o "Em … escreveu:" logo antes
  let ant = corte.previousElementSibling;
  while (ant && (ant.tagName === 'HR' || (ant.tagName === 'DIV' && ant.querySelector('hr') && !(ant.textContent || '').trim()) || RE_ESCREVEU.test((ant.textContent || '').trim()))) { corte = ant; ant = corte.previousElementSibling; }
  const range = doc.createRange();
  range.setStartBefore(corte);
  range.setEndAfter(body.lastChild);
  range.deleteContents();
  // tira linhas vazias/separadores que sobraram no fim
  while (body.lastChild && ((body.lastChild.nodeType === 3 && !body.lastChild.textContent.trim()) || (body.lastChild.nodeType === 1 && !body.lastChild.textContent.trim() && !body.lastChild.querySelector('img')))) body.removeChild(body.lastChild);
  if (!(body.textContent || '').trim() && !body.querySelector('img')) return null; // e-mail só com a citação (encaminhado): mostra tudo
  return { principal: body.innerHTML, citado: true };
}

/** Corpo do e-mail num iframe isolado (sem scripts) que cresce do tamanho do conteúdo, como no Gmail.
 *  Numa conversa, o histórico citado (as respostas anteriores coladas embaixo) fica atrás de um botão. */
function CorpoEmail({ html, texto, esconderCitacao }) {
  const ref = useRef(null);
  const [altura, setAltura] = useState(120);
  const [verCitado, setVerCitado] = useState(false);
  const partes = useMemo(() => (esconderCitacao ? separarCitacao(html, texto) : null), [html, texto, esconderCitacao]);
  const srcdoc = useMemo(() => {
    const corpo = partes && !verCitado ? partes.principal : (html || `<pre style="white-space:pre-wrap;font-family:inherit;margin:0">${escapar(texto)}</pre>`);
    return `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>html,body{margin:0;padding:0}body{font-family:Segoe UI,Arial,sans-serif;font-size:14px;color:#1f2937;word-wrap:break-word;overflow-x:auto}img{max-width:100%;height:auto}blockquote{margin:0 0 0 .8ex;border-left:2px solid #ccc;padding-left:1ex}table{max-width:100%}</style></head><body>${corpo}</body></html>`;
  }, [html, texto, partes, verCitado]);
  useEffect(() => { setAltura(120); }, [srcdoc]);
  // mede a altura do conteúdo e só muda se cresceu (mudar a altura do quadro mexe no layout do e-mail;
  // reagir a isso de novo fazia um vai-e-vem — por isso nada de ResizeObserver aqui)
  const ajustar = useCallback(() => {
    try {
      const doc = ref.current?.contentDocument;
      if (!doc?.body) return;
      const h = Math.min(20000, Math.max(60, doc.body.scrollHeight + 16));
      setAltura(atual => (h > atual + 2 ? h : atual));
    } catch (_) { /* sem acesso: fica a altura atual */ }
  }, []);
  const timers = useRef([]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  function carregou() {
    ajustar();
    try {
      const doc = ref.current.contentDocument;
      doc.querySelectorAll('img').forEach(img => img.addEventListener('load', ajustar));
      // mexer o mouse, rolar ou teclar DENTRO do e-mail conta como uso do Quality (senão o "Sair" automático
      // por 30 min sem uso derrubava quem estava lendo um e-mail longo)
      const avisarUso = () => window.dispatchEvent(new Event('mousemove'));
      ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart', 'scroll'].forEach(ev => doc.addEventListener(ev, avisarUso, { passive: true }));
    } catch (_) { /* idem */ }
    // imagens sem tamanho e fontes que chegam depois: confere de novo algumas vezes
    timers.current.forEach(clearTimeout);
    timers.current = [300, 1000, 2500].map(ms => setTimeout(ajustar, ms));
  }
  // allow-same-origin SEM allow-scripts: nada do e-mail executa; só deixa a tela medir a altura
  return (
    <>
      <iframe ref={ref} className="em-corpo" title="Conteúdo do e-mail" sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox" srcDoc={srcdoc} onLoad={carregou} style={{ height: altura }} />
      {partes && (
        <button type="button" className="em-citado-botao" onClick={() => setVerCitado(v => !v)} title={verCitado ? 'Esconder o histórico citado' : 'Mostrar as mensagens anteriores que vieram coladas neste e-mail'}>
          <span>•••</span> {verCitado ? 'Esconder histórico citado' : 'Mostrar histórico citado'}
        </button>
      )}
    </>
  );
}

/** Uma mensagem da conversa, aberta: cabeçalho, paciente, prazos, reunião, anexos e o corpo. */
function MensagemAberta({ resumo, contaId, eu, naConversa, onDados, onResponder, onAviso, onAbrirPaciente, onFechar }) {
  const [m, setM] = useState(null);
  const [erro, setErro] = useState('');
  const [buscarPac, setBuscarPac] = useState(false);
  const [verDetalhes, setVerDetalhes] = useState(false);
  useEffect(() => {
    email('ler', { contaId, pasta: resumo.pasta, uid: resumo.uid, marcarLido: true })
      .then(d => { setM(d); onDados(d); })
      .catch(e => setErro(e.message));
  }, [contaId, resumo.pasta, resumo.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  if (erro) return <div className="em-msg em-msg-aberta"><div className="em-aviso em-aviso-erro">{erro}</div></div>;
  if (!m) return <div className="em-msg em-msg-aberta"><div className="em-vazio"><Loader2 size={16} className="em-gira" /> Abrindo…</div></div>;
  const indice = m.indice;
  const ctx = { contaId, pasta: m.pasta, uid: m.uid };
  const atualizar = d => { setM(d); onDados(d); };
  const ehRascunho = /Rascunho|Draft/i.test(m.pasta) || m.flags.includes('\\Draft');

  async function vincular(p, remover) {
    setBuscarPac(false);
    try {
      const r = await email('vincular', { contaId, pasta: m.pasta, uid: m.uid, indiceId: indice?.id, paciente: { id: p.id, nome: p.nome, origem: p.origem }, remover });
      atualizar({ ...m, indice: { ...(indice || {}), id: r.indiceId, pacientes: r.pacientes } });
    } catch (e) { onAviso(e.message, 'erro'); }
  }
  async function marcarPrazo(p, feito) {
    try { const r = await email('prazoMarcar', { indiceId: indice.id, data: p.data, feito }); atualizar({ ...m, indice: { ...indice, prazos: r.prazos } }); }
    catch (e) { onAviso(e.message, 'erro'); }
  }
  async function prazoNaAgenda(p) {
    try {
      await email('paraAgenda', { indiceId: indice.id, prazo: true, dados: { titulo: `Prazo: ${m.assunto}`, data: p.data, horario: '09:00', horarioFim: '09:30', link: '' } });
      onAviso(`Prazo de ${diaCurto(p.data)} posto na sua agenda às 09:00.`);
    } catch (e) { onAviso(e.message, 'erro'); }
  }
  async function reuniaoNaAgenda(indiceId, dados) {
    try {
      await email('paraAgenda', { indiceId, dados });
      atualizar(await email('ler', { contaId, pasta: m.pasta, uid: m.uid, marcarLido: false }));
      onAviso('Posto na sua agenda.');
    } catch (e) { onAviso(e.message, 'erro'); throw e; }
  }
  // a mesma pessoa no IW e na Captação vira uma etiqueta só
  const pacientes = [];
  for (const p of indice?.pacientes || []) {
    const igual = pacientes.find(x => x.nome.trim().toUpperCase() === p.nome.trim().toUpperCase());
    if (igual) { igual.lista.push(p); if (!igual.origens.includes(p.origem)) igual.origens.push(p.origem); }
    else pacientes.push({ ...p, nome: p.nome.trim(), lista: [p], origens: [p.origem] });
  }

  return (
    <div className="em-msg em-msg-aberta">
      <div className="em-msg-cab">
        <Avatar pessoa={m.de[0]} />
        <div className="em-msg-quem" onClick={onFechar} title="Recolher">
          <div><strong>{m.de.map(nomeDe).join(', ')}</strong> <span className="em-sub">&lt;{m.de.map(p => p.email).join(', ')}&gt;</span></div>
          <button className="em-msg-para" onClick={e => { e.stopPropagation(); setVerDetalhes(v => !v); }}>
            para {[...m.para, ...m.cc].map(p => (p.email === eu.caixa ? 'mim' : (p.nome || p.email))).slice(0, 4).join(', ')}{m.para.length + m.cc.length > 4 ? '…' : ''} <ChevronDown size={12} />
          </button>
        </div>
        <span className="em-msg-data">{dataHora(m.data)}</span>
        {!ehRascunho && <button className="em-icone" title="Responder" onClick={() => onResponder('responder', m)}><Reply size={17} /></button>}
        {ehRascunho
          ? <button className="em-btn em-btn-mini em-btn-primario" onClick={() => onResponder('rascunho', m)}><Pencil size={13} /> Continuar editando</button>
          : <button className="em-icone" title="Encaminhar" onClick={() => onResponder('encaminhar', m)}><Forward size={17} /></button>}
      </div>
      {verDetalhes && (
        <div className="em-msg-detalhes">
          <div><span>De:</span> {m.de.map(p => `${p.nome} <${p.email}>`).join(', ')}</div>
          {m.para.length > 0 && <div><span>Para:</span> {m.para.map(p => (p.nome ? `${p.nome} <${p.email}>` : p.email)).join(', ')}</div>}
          {m.cc.length > 0 && <div><span>Cc:</span> {m.cc.map(p => (p.nome ? `${p.nome} <${p.email}>` : p.email)).join(', ')}</div>}
          {m.cco.length > 0 && <div><span>Cco:</span> {m.cco.map(p => p.email).join(', ')}</div>}
          <div><span>Data:</span> {dataHora(m.data)}</div>
          <div><span>Pasta:</span> {m.pasta}</div>
        </div>
      )}

      <div className="em-leitor-tags">
        {pacientes.map(p => (
          <span key={p.id} className={`em-pac ${p.confianca === 'verificar' && !p.manual ? 'em-pac-verificar' : ''}`} title={p.manual ? p.como : `achado pelo nome no e-mail (${p.confianca})`}>
            <button className="em-pac-nome" onClick={() => onAbrirPaciente({ id: p.id, ids: p.lista.map(x => x.id), nome: p.nome, origem: p.origem, origens: p.origens })}>{p.nome}</button>
            <span className="em-pac-origem">{p.origens.map(o => ORIGEM[o]).join(' + ')}</span>
            {p.confianca === 'verificar' && !p.manual && <span className="em-pac-origem">verificar</span>}
            <button className="em-pac-x" title="Não é este paciente" onClick={async () => { for (const x of p.lista) await vincular(x, true); }}><X size={11} /></button>
          </span>
        ))}
        <div className="em-rel">
          <button className="em-btn em-btn-mini" onClick={() => setBuscarPac(b => !b)}><UserPlus size={13} /> Paciente</button>
          {buscarPac && <BuscarPaciente onEscolher={p => vincular(p)} onFechar={() => setBuscarPac(false)} />}
        </div>
        {(indice?.prazos || []).map(p => (
          <span key={p.data} className={`em-prazo ${p.feito ? 'em-prazo-feito' : ''}`} title={p.trecho}>
            <AlarmClock size={13} /> Prazo {diaCurto(p.data)}
            <button title={p.feito ? 'Reabrir' : 'Marcar como resolvido'} onClick={() => marcarPrazo(p, !p.feito)}><Check size={12} /></button>
            {!p.feito && <button title="Pôr na minha agenda" onClick={() => prazoNaAgenda(p)}><CalendarPlus size={12} /></button>}
          </span>
        ))}
      </div>

      {indice?.convite && <CartaoReuniao indice={indice} eu={eu} onAgenda={reuniaoNaAgenda} />}

      <CorpoEmail html={m.html} texto={m.texto} esconderCitacao={naConversa} />

      {m.anexos.length > 0 && (
        <div className="em-msg-anexos">
          <div className="em-sub">{m.anexos.length} anexo(s)</div>
          <div className="em-leitor-anexos">
            {m.anexos.map(a => (
              <span key={a.indice} className="em-anexo em-anexo-grande">
                <Paperclip size={14} /> <span className="em-anexo-nome" title={a.nome}>{a.nome}</span> <span>{tamanho(a.tamanho)}</span>
                {/^(application\/pdf|image\/|text\/plain)/.test(a.tipo || '') && <button title="Abrir" onClick={() => baixarAnexo({ ...ctx, indice: a.indice }, a.nome, true).catch(e => onAviso(e.message, 'erro'))}><Eye size={14} /></button>}
                <button title="Baixar" onClick={() => baixarAnexo({ ...ctx, indice: a.indice }, a.nome).catch(e => onAviso(e.message, 'erro'))}><Download size={14} /></button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Janela da conversa (como no Gmail): o assunto no topo, as mensagens em ordem — as antigas recolhidas
 * numa linha, a última (e as não lidas) abertas —, e Responder / Encaminhar no fim.
 */
export default function Conversa({ alvo, eu, pastas, onFechar, onMudou, onResponder, onAviso, onAbrirPaciente }) {
  const [conv, setConv] = useState(null);
  const [erro, setErro] = useState('');
  const [abertas, setAbertas] = useState(new Set());
  const [verTodas, setVerTodas] = useState(false);
  const [moverAberto, setMoverAberto] = useState(false);
  const dados = useRef({});
  const chave = r => `${r.pasta}|${r.uid}`;

  useEffect(() => {
    setConv(null); setErro(''); setVerTodas(false); dados.current = {};
    email('conversa', { contaId: alvo.contaId, pasta: alvo.pasta, uid: alvo.uid, messageId: alvo.messageId })
      .then(c => {
        setConv(c);
        const ab = new Set();
        const ultima = c.mensagens[c.mensagens.length - 1];
        if (ultima) ab.add(chave(ultima));
        for (const m of c.mensagens) if (m.atual || !m.lido) ab.add(chave(m));
        setAbertas(ab);
      })
      .catch(e => setErro(e.message));
  }, [alvo.contaId, alvo.pasta, alvo.uid, alvo.messageId]);

  useEffect(() => {
    const esc = e => { if (e.key === 'Escape' && !document.querySelector('.em-compor')) onFechar(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onFechar]);

  const msgs = conv?.mensagens || [];
  const naPasta = msgs.filter(m => m.pasta === alvo.pasta || (alvo.messageId && m.atual));
  const pastaDaConversa = naPasta[0]?.pasta || alvo.pasta;
  const sinalizada = naPasta.some(m => m.sinalizado);

  async function emTodas(como, destino) {
    const uids = naPasta.map(m => m.uid);
    if (!uids.length) return;
    setMoverAberto(false);
    try {
      if (como === 'excluir') await email('mover', { contaId: alvo.contaId, pasta: pastaDaConversa, uids, papel: '\\Trash' });
      else if (como === 'mover') await email('mover', { contaId: alvo.contaId, pasta: pastaDaConversa, uids, destino });
      else await email('marcar', { contaId: alvo.contaId, pasta: pastaDaConversa, uids, como });
      onMudou();
      if (['excluir', 'mover', 'naoLido'].includes(como)) {
        onAviso({ excluir: 'Conversa enviada para a lixeira.', mover: `Conversa movida para ${pastas.find(p => p.caminho === destino)?.nome || destino}.`, naoLido: 'Marcada como não lida.' }[como]);
        onFechar();
      } else setConv(c => ({ ...c, mensagens: c.mensagens.map(m => (uids.includes(m.uid) && m.pasta === pastaDaConversa ? { ...m, sinalizado: como === 'sinalizar' } : m)) }));
    } catch (e) { onAviso(e.message, 'erro'); }
  }

  /** Responder / encaminhar: usa os dados já abertos da mensagem (ou abre agora). */
  async function responder(modo, alvoMsg) {
    let d = alvoMsg?.html !== undefined ? alvoMsg : null;
    const ref = alvoMsg && !d ? alvoMsg : [...msgs].reverse().find(m => !m.enviado) || msgs[msgs.length - 1];
    if (!d) d = dados.current[chave(ref)] || await email('ler', { contaId: alvo.contaId, pasta: ref.pasta, uid: ref.uid, marcarLido: false }).catch(e => { onAviso(e.message, 'erro'); return null; });
    if (d) onResponder(modo, { ...d, contaId: alvo.contaId });
  }

  // muitas mensagens recolhidas no meio: mostra a primeira, "N anteriores" e as últimas (como no Gmail)
  let visiveis = msgs.map((m, i) => ({ m, i }));
  let escondidas = 0;
  if (!verTodas && msgs.length > 4) {
    const fechadasMeio = msgs.slice(1, -2).filter(m => !abertas.has(chave(m)));
    if (fechadasMeio.length >= 2) {
      visiveis = msgs.map((m, i) => ({ m, i })).filter(({ m, i }) => i === 0 || i >= msgs.length - 2 || abertas.has(chave(m)));
      escondidas = msgs.length - visiveis.length;
    }
  }

  return (
    <div className="em-modal-fundo" onMouseDown={e => { if (e.target === e.currentTarget) onFechar(); }}>
      <div className="em-conversa">
        <div className="em-conversa-barra">
          <button className="em-icone" title="Excluir conversa" onClick={() => emTodas('excluir')}><Trash2 size={18} /></button>
          <button className="em-btn em-btn-mini" title="Volta a aparecer em negrito na lista (útil se abriu sem querer)" onClick={() => emTodas('naoLido')}><Mail size={15} /> Marcar como não lido</button>
          <button className={`em-icone ${sinalizada ? 'em-estrela' : ''}`} title={sinalizada ? 'Tirar sinal' : 'Sinalizar'} onClick={() => emTodas(sinalizada ? 'tirarSinal' : 'sinalizar')}><Star size={18} fill={sinalizada ? 'currentColor' : 'none'} /></button>
          <div className="em-rel">
            <button className="em-icone" title="Mover para pasta" onClick={() => setMoverAberto(a => !a)}><FolderInput size={18} /></button>
            {moverAberto && <div className="em-menu em-menu-esq">{pastas.filter(p => p.caminho !== pastaDaConversa).map(p => <button key={p.caminho} style={{ paddingLeft: 10 + p.nivel * 12 }} onClick={() => emTodas('mover', p.caminho)}>{p.nome}</button>)}</div>}
          </div>
          {msgs.length > 1 && <span className="em-sub">{msgs.length} mensagens</span>}
          <button className="em-icone em-dir" title="Fechar (Esc)" onClick={onFechar}><X size={20} /></button>
        </div>
        <div className="em-conversa-corpo">
          {erro && <div className="em-aviso em-aviso-erro">{erro}</div>}
          {!conv && !erro && <div className="em-vazio"><Loader2 size={18} className="em-gira" /> Abrindo conversa…</div>}
          {conv && (
            <>
              <h2 className="em-conversa-assunto">{conv.assunto}</h2>
              {visiveis.map(({ m, i }, k) => (
                <React.Fragment key={chave(m)}>
                  {escondidas > 0 && k === 1 && (
                    <button className="em-msg-escondidas" onClick={() => setVerTodas(true)}><span>{escondidas}</span> mensagens anteriores</button>
                  )}
                  {abertas.has(chave(m)) ? (
                    <MensagemAberta resumo={m} contaId={alvo.contaId} eu={eu} naConversa={msgs.length > 1}
                      onDados={d => { dados.current[chave(m)] = d; if (!m.lido) { m.lido = true; onMudou(true); } }}
                      onResponder={responder} onAviso={onAviso} onAbrirPaciente={onAbrirPaciente}
                      onFechar={() => msgs.length > 1 && setAbertas(s => { const n = new Set(s); n.delete(chave(m)); return n; })} />
                  ) : (
                    <div className={`em-msg em-msg-fechada ${m.lido ? '' : 'em-nao-lido'}`} onClick={() => setAbertas(s => new Set(s).add(chave(m)))}>
                      <Avatar pessoa={m.de[0]} />
                      <strong className="em-msg-nome">{m.enviado ? 'Eu' : m.de.map(nomeDe).join(', ')}</strong>
                      <span className="em-msg-resumo">{m.resumo || (m.anexo ? '(anexo)' : '')}</span>
                      {m.anexo && <Paperclip size={13} className="em-sub" />}
                      <span className="em-msg-data">{quando(m.data)}</span>
                    </div>
                  )}
                </React.Fragment>
              ))}
              <div className="em-conversa-resp">
                <button className="em-btn em-btn-redondo" onClick={() => responder('responder')}><Reply size={16} /> Responder</button>
                <button className="em-btn em-btn-redondo" onClick={() => responder('responderTodos')}><ReplyAll size={16} /> Responder a todos</button>
                <button className="em-btn em-btn-redondo" onClick={() => responder('encaminhar')}><Forward size={16} /> Encaminhar</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
